import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface PortalLog {
  field: string;
  value: string;
  created_at: string;
  user_name: string;
  org_name: string;
}

export const DevicePortalChanges = ({ deviceId }: { deviceId: string }) => {
  const { data = [], isLoading, isFetching, refetch, error } = useQuery<PortalLog[]>({
    queryKey: ["device-portal-changes", deviceId],
    queryFn: async () => {
      const { data: logs, error } = await (supabase as any)
        .from("device_change_log")
        .select("*")
        .eq("device_id", deviceId)
        .order("created_at", { ascending: false })
        .limit(40);
      if (error) throw error;
      if (!logs?.length) return [];
      const userIds = [...new Set(logs.map((l: any) => l.user_id).filter(Boolean))] as string[];
      const orgIds = [...new Set(logs.map((l: any) => l.organization_id).filter(Boolean))] as string[];
      const [p, o] = await Promise.all([
        userIds.length
          ? supabase.from("profiles").select("user_id, first_name, last_name").in("user_id", userIds)
          : Promise.resolve({ data: [] as any[] }),
        orgIds.length
          ? supabase.from("organizations").select("id, name").in("id", orgIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      const pm = new Map<string, string>();
      (p.data || []).forEach((x: any) =>
        pm.set(x.user_id, [x.first_name, x.last_name].filter(Boolean).join(" ") || "Unknown")
      );
      const om = new Map<string, string>();
      (o.data || []).forEach((x: any) => om.set(x.id, x.name || "Unknown"));
      return logs.map((l: any) => ({
        field: l.field,
        value: l.value,
        created_at: l.created_at,
        user_name: l.user_id ? pm.get(l.user_id) || "Unknown" : "System",
        org_name: l.organization_id ? om.get(l.organization_id) || "Unknown" : "—",
      }));
    },
  });

  return (
    <div className="rounded border bg-muted/30 p-2 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="text-muted-foreground">
          {data.length} change{data.length !== 1 ? "s" : ""} recorded
        </span>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          <span className="ml-1 text-xs">Refresh</span>
        </Button>
      </div>
      {isLoading ? (
        <div className="text-muted-foreground py-1">Loading change logs…</div>
      ) : error ? (
        <div className="text-destructive py-1">Failed to load portal changes.</div>
      ) : data.length === 0 ? (
        <div className="text-muted-foreground py-1">No change logs found for this device.</div>
      ) : (
        <div className="max-h-64 overflow-auto space-y-1">
          {data.map((l, i) => (
            <div key={i} className="flex flex-wrap gap-x-2 border-b last:border-0 py-1">
              <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
              <span className="font-medium">{l.field}</span>
              <span className="break-all">→ {l.value}</span>
              <span className="text-muted-foreground ml-auto">
                {l.user_name} · {l.org_name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DevicePortalChanges;
